// Enums
export enum ToneType {
  Friendly = 'Friendly',
  Luxurious = 'Luxurious',
  Humorous = 'Humorous',
  Encouraging = 'Encouraging',
  Enthusiastic = 'Enthusiastic',
  Confident = 'Confident'
}

export enum VoiceType {
  Informative = 'Informative',
  Persuasive = 'Persuasive',
  Sustainable = 'Sustainable',
  Healthy = 'Healthy',
  Playful = 'Playful',
  Supportive = 'Supportive'
}

export enum VoiceAndToneMatchType {
  FullMatch = 'FullMatch',
  ToneMismatch = 'ToneMismatch',
  VoiceMismatch = 'VoiceMismatch',
  FullMismatch = 'FullMismatch'
}

export enum ContentMetricType {
  ContentScore = 'ContentScore',
  ContentUtilizationScore = 'ContentUtilizationScore',
  ContentAccuracyScore = 'ContentAccuracyScore',
  KeywordPerformanceScore = 'KeywordPerformanceScore',
  KeywordUsageScore = 'KeywordUsageScore',
  KeywordProductivityScore = 'KeywordProductivityScore',
  KeywordPopularityScore = 'KeywordPopularityScore',
  VoiceMatchScore = 'VoiceMatchScore',
  ToneMatchScore = 'ToneMatchScore',
  VoiceAndToneMatchAnalysis = 'VoiceAndToneMatchAnalysis',
  KeywordOrganicTraffic = 'KeywordOrganicTraffic',
  KeywordSearchVolume = 'KeywordSearchVolume'
}

export enum CategoryType {
  Department = 'Department',
  Category = 'Category',
  Subcategory = 'Subcategory',
  Brand = 'Brand'
}

export interface TimePeriodRange {
  startWeek: number;
  endWeek: number;
}

// Requests
export interface BaseMetricsRequest {
  retailerId: number;
  stacklineSkus: string[];
  categoryIds: number[];
  brandIds: number[];
  subcategoryIds: number[];
  segmentIds: string[];
  businessUnits: string[];
  startWeekId: number;
  endWeekId: number;
  resultCount: number;
  sortField: string;
  offset?: number;
}

export interface GetScoreForContentMetricRequest extends BaseMetricsRequest {
  contentMetricType: ContentMetricType;
  voiceAndToneMatchTypes: VoiceAndToneMatchType[];
}

export interface GetScoreForContentMetricByCategoryTypeRequest extends BaseMetricsRequest {
  contentMetricType: ContentMetricType;
  categoryType: CategoryType;
}

export interface GetContentMetricsForProductRequest {
  retailerSku: string;
  retailerId: number;
  stacklineSku: string;
  weekId: number;
}

export interface VoiceAndToneMetricsRequest extends BaseMetricsRequest {
  voiceAndToneMatchTypes: VoiceAndToneMatchType[];
  voiceTypes: VoiceType[];
  toneTypes: ToneType[];
}

export interface GetWeightedKeywordsAndPhrasesForProductRequest {
  retailerSku: string;
  retailerId: number;
  weekId: number;
  contentMetricType: ContentMetricType.KeywordOrganicTraffic | ContentMetricType.KeywordSearchVolume;
}

// Responses
export interface ContentMetricScoreByWeekRecord {
  weekId: number;
  contentMetricType: ContentMetricType;
  score: number;
}

export interface GetContentScoreSummaryResponse {
  scores: ContentSummaryScores;
}

export interface ContentScoreByWeekRecord {
  weekId: number;
  scores: ContentSummaryScores;
}

export interface VoiceAndToneMatchAnalysisRecord {
  stacklineSku: string;
  scores: VoiceAndToneScores;
}

export interface GetContentScoreExtendedSummaryByWeekResponse {
  records: ContentScoreByWeekRecord[];
}

type ProductCountByVoiceRecord = {
  type: VoiceType;
  count: number;
};

type ProductCountByToneRecord = {
  type: ToneType;
  count: number;
};

export type ProductCountByVoiceOrToneRecord = ProductCountByVoiceRecord | ProductCountByToneRecord;

export interface GetProductCountByVoiceTypeResponse {
  records: ProductCountByVoiceRecord[];
}

export interface GetProductCountByToneTypeResponse {
  records: ProductCountByToneRecord[];
}

type ContentSummaryScores = {
  contentScore: number;
  contentUtilizationScore: number;
  contentAccuracyScore: number;
  keywordUsageScore: number;
  keywordProductivityScore: number;
  keywordPopularityScore: number;
  voiceMatchScore: number;
  toneMatchScore: number;
};

export type VoiceAndToneScores = {
  currentDominantVoiceType: VoiceType;
  currentDominantToneType: ToneType;
  expectedVoice: VoiceType;
  voiceMatchScore: number;
  expectedTone: ToneType;
  toneMatchScore: number;
  voiceAndToneMatchType: VoiceAndToneMatchType;
};

type BaseContentScoreKeys =
  | 'aPlusScore'
  | 'descriptionScore'
  | 'featureBulletsScore'
  | 'imagesScore'
  | 'titleScore'
  | 'videosScore';

export type BaseContentScores = {
  [K in BaseContentScoreKeys]: number;
};

type ContentUtilizationScores = {
  contentUtilizationScore: number;
} & BaseContentScores;

type ContentAccuracyScores = {
  contentAccuracyScore: number;
} & BaseContentScores;

export interface GetContentUtilizationSummaryResponse {
  contentUtilizationScores: ContentUtilizationScores;
}

export type KeywordPerformanceScores = {
  keywordUsageScore: number;
  keywordProductivityScore: number;
  keywordPopularityScore: number;
  averageCountOfKeywords: number;
  averageCountOfKeywordsGeneratingTraffic: number;
  averageVolumeOfSearchTraffic: number;
};

export type ContentScores =
  | ContentUtilizationScores
  | ContentAccuracyScores
  | ContentSummaryScores
  | VoiceAndToneScores;

export interface GetContentAccuracySummaryResponse {
  contentAccuracyScores: ContentAccuracyScores;
}

export interface GetKeywordPerformanceScoreSummaryResponse {
  scores: KeywordPerformanceScores;
}

export type ContentMetricScoreByProductRecord = {
  stacklineSku: string;
  scores: ContentScores;
};

export interface GetScoreForContentMetricByProductResponse {
  records: ContentMetricScoreByProductRecord[];
}

export interface GetScoreForContentMetricByWeekResponse {
  records: ContentMetricScoreByWeekRecord[];
}

export interface GetContentUtilizationDetailMetricsForProductResponse {
  records: ProductContentUtilizationRecord[];
}

export interface GetContentUtilizationSummaryMetricsForProductResponse {
  retailerId: number;
  weekId: number;
  stacklineSku: string;
  scores: ContentUtilizationScores;
}

export interface GetContentAccuracySummaryMetricsForProductResponse {
  retailerId: number;
  weekId: number;
  stacklineSku: string;
  scores: ContentAccuracyScores;
}

export interface GetContentAccuracyDetailMetricsForProductResponse {
  records: ProductContentAccuracyRecord[];
}

interface CategoryScore {
  id: number;
  score: number;
  contentMetricType: ContentMetricType;
  categoryType: CategoryType;
}

export interface GetScoreForContentMetricByCategoryTypeResponse {
  records: CategoryScore[];
}

export interface GetContentUtilizationScoreByProductResponse {
  data: any;
}

export interface GetContentAccuracyScoreSummaryResponse {
  data: any;
}

export interface GetContentAccuracyScoreByProductResponse {
  data: any;
}

export interface GetVoiceAndToneAnalysisByProductResponse {
  records: {
    stacklineSku: string;
    currentDominantVoiceType: string;
    currentDominantToneType: string;
    expectedVoice: string;
    voiceMatchScore: number;
    expectedTone: string;
    toneMatchScore: number;
    voiceAndToneMatchType: string;
  }[];
}

export interface GetVoiceAndToneCurrentAndTargetVectorsResponse {
  retailerId: number;
  weekId: number;
  retailerSku: string;
  stacklineSku: string;
  currentVoiceVector: number[];
  targetVoiceVector: number[];
  currentToneVector: number[];
  targetToneVector: number[];
  targetDominantVoice: VoiceType;
  currentDominantVoice: VoiceType;
  targetDominantTone: ToneType;
  currentDominantTone: ToneType;
  voiceMatchScore: number;
  toneMatchScore: number;
}

export interface GetWeightedKeywordsAndPhrasesForProductResponse {
  retailerId: number;
  retailerSku: string;
  isSuccess: boolean;
  message: string;
  weightedKeywordPhrases: {
    retailerId: number;
    retailerSku: string;
    isSuccess: boolean;
    message: string;
    weightedKeywordPhrases: {
      sections: WeightedKeywordsAndPhrasesSection[];
    };
  };
}

// Product Level Content Utilization and Accuracy
export enum EntityItemType {
  BulletPoint = 'BulletPoint',
  BulletPointRecord = 'BulletPointRecord',
  Title = 'Title',
  Description = 'Description',
  Image = 'Image',
  ImageRecord = 'ImageRecord',
  Video = 'Video',
  VideoRecord = 'VideoRecord',
  APlusContent = 'APlusContent'
}

export interface ImageMetadata {
  width: number;
  height: number;
  unit: string;
}

export interface VideoMetadata extends ImageMetadata {
  duration: number;
}

interface EntityItem {
  entityValue: string;
  entityMetadata: ImageMetadata | VideoMetadata | null;
}

interface ProductContentUtilizationMetric {
  expectedCount: number;
  actualCount: number;
  score: number;
}

export type ProductContentUtilizationRecord = EntityItem & {
  entityType: EntityItemType;
  metrics: ProductContentUtilizationMetric;
  children: ProductContentUtilizationRecord[] | null;
};

export interface ProductContentAccuracyRecord {
  entityType: EntityItemType;
  currentEntity: EntityItem;
  approvedEntity: EntityItem;
  isMatch: boolean;
}

export interface WeightedKeywordPhrase {
  phrase: string;
  groupId?: string;
}

interface WeightedKeyword {
  keyword: string;
  weight: number;
  subcat_level_weight: number;
  sku_level_weight: number;
}

export interface WeightedKeywordGroup {
  groupId: string;
  totalWeight: number;
  totalSubcatWeight: number;
  weights: WeightedKeyword[];
}

export interface WeightedKeywordsAndPhrasesSection {
  name: string;
  content: WeightedKeywordPhrase[];
  groups: WeightedKeywordGroup[];
}

// TODO: confirm with backend which fields are optional
export interface GetContentResponse {
  retailerId: number;
  weekId: number;
  retailerSku: string;
  stacklineSku: string;
  timestamp: number;
  bullets?: string[];
  title?: string;
  description?: string;
  imageUrls?: string[];
  videoUrls?: string[];
  aplusScore?: number;
}
